import { useState, useEffect, useRef } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Save, Check, Edit2, AlertCircle, Info, Download, Upload, X } from "lucide-react";
import {
  buildRateCardWorkbook, downloadRateCard, parseRateCardFile,
  diffFmLm, diffMm, diffOda,
} from "@/utils/rateCardExcel";

const FM_LM_DEFAULTS = {
  rate_per_kg: 2, free_km: 15, rate_per_km_beyond: 20, min_charge: 50,
};

const MM_BANDS = ["0-50", "50-200", "200-400", "400-900", "900-1500", "1500-2500", "2500+"];
const MM_SLABS = ["0-20", "20-50", "50-100", "100-200", "200-300", "300-500", "500-1000"];

const B2B_REGIONS = ["N1", "N2", "E", "NE", "W1", "W2", "S1", "S2", "CTR"];
const B2B_MATRIX = {
  N1:  { N1: 6, N2: 6, E: 11, NE: 16.5, W1: 8, W2: 9, S1: 11, S2: 14, CTR: 8 },
  N2:  { N1: 6, N2: 6, E: 11, NE: 16.5, W1: 9, W2: 9, S1: 11, S2: 14, CTR: 9 },
  E:   { N1: 9, N2: 11, E: 6, NE: 8,    W1: 9, W2: 11, S1: 9, S2: 12, CTR: 8 },
  NE:  { N1: 9, N2: 11, E: 8, NE: 6,    W1: 11, W2: 11, S1: 11, S2: 16.5, CTR: 9 },
  W1:  { N1: 8, N2: 9, E: 11, NE: 16.5, W1: 6, W2: 6, S1: 9, S2: 12, CTR: 8 },
  W2:  { N1: 9, N2: 9, E: 11, NE: 16.5, W1: 6, W2: 6, S1: 8, S2: 11, CTR: 8 },
  S1:  { N1: 9, N2: 11, E: 11, NE: 16.5, W1: 9, W2: 8, S1: 6, S2: 8, CTR: 8 },
  S2:  { N1: 11, N2: 11, E: 11, NE: 16.5, W1: 9, W2: 9, S1: 6, S2: 6, CTR: 8 },
  CTR: { N1: 8, N2: 9, E: 11, NE: 16.5, W1: 6, W2: 8, S1: 8, S2: 11, CTR: 6 },
};

const TABS = [
  { key: "fmlm", label: "FM / LM Rates" },
  { key: "mm", label: "MM Matrix" },
  { key: "tpl", label: "3PL Config" },
  { key: "products", label: "Product Categories" },
];

const SERVICE_BADGE = {
  ALL: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PRIORITY_ONLY: "bg-amber-100 text-amber-700 border-amber-200",
  BLOCKED: "bg-red-100 text-red-700 border-red-200",
};

// ─────────────────────────────────────────────────────────────────────────────
// FM / LM Rates Tab
// ─────────────────────────────────────────────────────────────────────────────
function FmLmCard({ label, values, onChange, onSave, saving, updatedAt }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`fmlm-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <h3 className="font-heading text-sm font-bold text-slate-900 uppercase mb-4">{label}</h3>
      <div className="space-y-3">
        {[
          { k: "rate_per_kg", lab: "Rate per kg (₹)" },
          { k: "free_km", lab: "Free KM Threshold" },
          { k: "rate_per_km_beyond", lab: "Rate per KM Beyond (₹)" },
          { k: "min_charge", lab: "Minimum Charge (₹)" },
        ].map(({ k, lab }) => (
          <div key={k} className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs font-semibold text-slate-500 uppercase">{lab}</label>
            <input
              type="number" step="0.01"
              value={values[k] ?? ""}
              onChange={(e) => onChange(k, e.target.value)}
              data-testid={`fmlm-${label.toLowerCase().replace(/\s+/g, "-")}-${k}`}
              className="col-span-2 text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-brand-500"
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="text-[10px] text-slate-400">
          {updatedAt ? `Last updated: ${new Date(updatedAt).toLocaleString("en-IN")}` : "Not yet saved"}
        </div>
        <button onClick={onSave} disabled={saving}
          data-testid={`fmlm-save-${label.toLowerCase()}`}
          className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function FmLmTab() {
  const [fm, setFm] = useState({ ...FM_LM_DEFAULTS });
  const [lm, setLm] = useState({ ...FM_LM_DEFAULTS });
  const [fmSaved, setFmSaved] = useState(null);
  const [lmSaved, setLmSaved] = useState(null);
  const [savingFm, setSavingFm] = useState(false);
  const [savingLm, setSavingLm] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v2/admin-config/fm-lm-rates");
        if (r.data?.fm) setFm({ ...FM_LM_DEFAULTS, ...r.data.fm });
        if (r.data?.lm) setLm({ ...FM_LM_DEFAULTS, ...r.data.lm });
        setFmSaved(r.data?.fm?.updated_at);
        setLmSaved(r.data?.lm?.updated_at);
      } catch { /* endpoint may not exist yet — fall back to defaults */ }
    })();
  }, []);

  const save = async (which) => {
    const rawValues = which === "fm" ? fm : lm;
    const numericValues = Object.fromEntries(
      Object.entries(rawValues).map(([k, v]) => [k, parseFloat(v) || 0])
    );
    const body = which === "fm" ? { fm: numericValues } : { lm: numericValues };
    const setSaving = which === "fm" ? setSavingFm : setSavingLm;
    const setSavedAt = which === "fm" ? setFmSaved : setLmSaved;
    setSaving(true);
    try {
      await api.put("/v2/admin-config/fm-lm-rates", body);
      toast.success(`${which.toUpperCase()} rates saved`);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed — endpoint not available");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FmLmCard label="First Mile" values={fm} onChange={(k, v) => setFm((s) => ({ ...s, [k]: v }))}
          onSave={() => save("fm")} saving={savingFm} updatedAt={fmSaved} />
        <FmLmCard label="Last Mile" values={lm} onChange={(k, v) => setLm((s) => ({ ...s, [k]: v }))}
          onSave={() => save("lm")} saving={savingLm} updatedAt={lmSaved} />
      </div>
      <div className="flex items-start gap-2 p-4 rounded-lg bg-blue-50 border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Customer rate agreements override these values. System always applies the higher of the two.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MM Matrix Tab
// ─────────────────────────────────────────────────────────────────────────────
function MMCell({ band, slab, value, onSaved }) {
  const [v, setV] = useState(value);
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => setV(value), [value]);
  const handleBlur = async () => {
    if (parseFloat(v) === parseFloat(value)) return;
    try {
      await api.put(`/v2/mm-matrix/${band}/${slab}`, { rate_per_kg: parseFloat(v) });
      setSavedFlash(true);
      onSaved?.();
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      toast.error("Save failed");
      setV(value);
    }
  };
  return (
    <div className="relative">
      <input type="number" step="0.5"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={handleBlur}
        data-testid={`mm-cell-${band}-${slab}`}
        style={{ width: "56px" }}
        className="text-xs border border-slate-200 rounded px-1.5 py-1 text-center focus:outline-none focus:border-brand-500"
      />
      {savedFlash && <Check className="w-3 h-3 text-emerald-500 absolute -right-1 -top-1" />}
    </div>
  );
}

function MMMatrixTab() {
  const [grid, setGrid] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/mm-matrix");
      setGrid(r.data?.grid || {});
    } catch { toast.error("Failed to load MM matrix"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900">Mid-Mile Rate Matrix (₹/kg)</h2>
      </div>
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          Internal reference only — customers never see mid-mile charges. Billed as per rate agreement.
        </p>
      </div>
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto p-4">
          <table className="w-full" data-testid="mm-matrix-table">
            <thead>
              <tr>
                <th className="text-left text-xs font-bold text-slate-500 uppercase p-2"></th>
                {MM_SLABS.map((s) => (
                  <th key={s} className="text-xs font-bold text-slate-500 uppercase p-2 text-center">{s}kg</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MM_BANDS.map((band) => (
                <tr key={band} className="border-t border-slate-100">
                  <td className="text-xs font-bold text-slate-600 p-2 whitespace-nowrap">{band} km</td>
                  {MM_SLABS.map((slab) => (
                    <td key={slab} className="p-2 text-center">
                      <MMCell band={band} slab={slab} value={grid[band]?.[slab] ?? 0} onSaved={load} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 italic">&gt;1000kg = FTL — handled manually by ops</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3PL Config Tab
// ─────────────────────────────────────────────────────────────────────────────
function TplTab() {
  const [markup, setMarkup] = useState("20");
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v2/admin-config/tpl-markup");
        if (r.data?.value != null) setMarkup(String(r.data.value));
        if (r.data?.updated_at) setUpdatedAt(r.data.updated_at);
      } catch { /* default 20 */ }
    })();
  }, []);

  const saveMarkup = async () => {
    setSaving(true);
    try {
      await api.put("/v2/admin-config/tpl-markup", { value: parseFloat(markup) || 0 });
      toast.success("Markup saved");
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed — endpoint not available");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* B2B Matrix */}
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Delhivery B2B — Zone Rate Matrix</h2>
        <p className="text-xs text-slate-400 italic">Delhivery contracted rates — not editable</p>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-x-auto p-4">
        <table className="w-full" data-testid="b2b-matrix-table">
          <thead>
            <tr>
              <th className="text-xs font-bold text-slate-500 uppercase p-2"></th>
              {B2B_REGIONS.map((r) => (
                <th key={r} className="text-xs font-bold text-slate-500 uppercase p-2 text-center">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {B2B_REGIONS.map((row) => (
              <tr key={row} className="border-t border-slate-200">
                <td className="text-xs font-bold text-slate-600 p-2">{row}</td>
                {B2B_REGIONS.map((col) => (
                  <td key={col} className="p-2 text-center text-xs text-slate-700 font-semibold">
                    {B2B_MATRIX[row][col]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="font-heading text-sm font-bold text-slate-900 uppercase mb-3">Sendtoo Markup on 3PL (%)</h3>
        <div className="flex items-center gap-3">
          <input type="number" step="0.5" min="0"
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            data-testid="tpl-markup-input"
            className="w-32 text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-brand-500"
          />
          <button onClick={saveMarkup} disabled={saving}
            data-testid="tpl-markup-save"
            className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
          </button>
          {updatedAt && (
            <span className="text-[10px] text-slate-400">
              Last updated: {new Date(updatedAt).toLocaleString("en-IN")}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3 italic">
          Customer agreements override this. System always applies the higher value.
        </p>
      </div>

      {/* 3PL Vendor Directory */}
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">3PL Vendor Directory</h2>
        <p className="text-xs text-slate-500">Currently configured 3PL partners</p>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="vendor-directory">
        <table className="w-full text-sm" data-testid="vendor-directory-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Vendor Code", "Vendor Name", "Rate Type", "Markup %", "Status", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr data-testid="vendor-row-DEL" className="hover:bg-slate-50">
              <td className="px-4 py-2 font-mono text-xs text-slate-700">DEL</td>
              <td className="px-4 py-2 font-semibold text-slate-800">Delhivery</td>
              <td className="px-4 py-2 text-xs text-slate-600">ZONE_MATRIX</td>
              <td className="px-4 py-2">
                <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-800">{markup || 0}%</span>
              </td>
              <td className="px-4 py-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200">
                  Active
                </span>
              </td>
              <td className="px-4 py-2">
                <button
                  onClick={() => document.querySelector('[data-testid="tpl-markup-input"]')?.focus()}
                  data-testid="vendor-edit-markup-DEL"
                  className="text-xs text-brand-500 hover:text-brand-700 font-semibold flex items-center gap-1"
                >
                  <Edit2 className="w-3 h-3" /> Edit Markup
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Additional 3PL partners (Bluedart, India Post, DHL etc.) can be onboarded here when contracted. Each vendor&apos;s rate matrix is managed separately once activated.
        </p>
      </div>
      <div>
        <button
          disabled
          data-testid="vendor-add-disabled"
          title="Contact Sendtoo tech team to onboard a new 3PL vendor"
          className="px-4 py-2 bg-slate-100 text-slate-400 text-xs font-semibold rounded cursor-not-allowed border border-slate-200"
        >
          + Add Vendor
        </button>
      </div>

      {/* B2C Reference */}
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Delhivery B2C — Zone Reference</h2>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-2">
        {[
          ["Zone A", "Same city"],
          ["Zone B", "Same region, different city"],
          ["Zone C1 / C2", "Cross-region (short / medium)"],
          ["Zone D1 / D2", "Cross-region (long)"],
          ["Zone E", "NE states destination"],
          ["Zone F", "J&K / Ladakh destination"],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0">
            <span className="font-semibold text-slate-700">{k}</span>
            <span className="text-slate-500">{v}</span>
          </div>
        ))}
        <div className="flex flex-wrap gap-3 pt-3 text-xs">
          <span className="px-2 py-1 bg-slate-100 rounded">Divisor: 5000</span>
          <span className="px-2 py-1 bg-slate-100 rounded">FSC: 5%</span>
          <span className="px-2 py-1 bg-slate-100 rounded">Express: ×1.3</span>
          <span className="px-2 py-1 bg-slate-100 rounded">Max: 10kg</span>
        </div>
        <p className="text-xs text-slate-400 italic pt-2">Auto-calculated at quote time</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Products Tab
// ─────────────────────────────────────────────────────────────────────────────
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/products/categories");
      setItems(r.data || []);
    } catch { toast.error("Failed to load categories"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async (code) => {
    try {
      await api.put(`/v2/products/categories/${code}`, {
        insurance_rate_pct: parseFloat(editValue),
      });
      toast.success(`${code} updated`);
      setEditingCode(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Category Name", "Code", "Service Restriction", "Insurance %", "Actions"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => (
                <tr key={it.code} data-testid={`product-row-${it.code}`}
                  className={`hover:bg-slate-50 ${it.code === "HAZARDOUS" ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-2 font-semibold text-slate-800">{it.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{it.code}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${SERVICE_BADGE[it.service_restriction] || SERVICE_BADGE.ALL}`}>
                      {it.service_restriction}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {editingCode === it.code ? (
                      <input type="number" step="0.05" min="0"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => save(it.code)}
                        onKeyDown={(e) => e.key === "Enter" && save(it.code)}
                        data-testid={`product-insurance-input-${it.code}`}
                        className="w-20 text-sm border border-brand-500 rounded px-1.5 py-0.5 focus:outline-none"
                      />
                    ) : (
                      <span className="text-sm text-slate-700">{Number(it.insurance_rate_pct).toFixed(2)}%</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => { setEditingCode(it.code); setEditValue(it.insurance_rate_pct); }}
                      disabled={editingCode === it.code}
                      data-testid={`product-edit-${it.code}`}
                      className="p-1.5 text-slate-400 hover:text-brand-500 rounded">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-500 italic">
        Insurance rates apply unless overridden in customer rate agreement.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function RateEngine() {
  const [tab, setTab] = useState("fmlm");
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [excelErrors, setExcelErrors] = useState(null);
  const [excelDiff, setExcelDiff] = useState(null);
  const [excelParsed, setExcelParsed] = useState(null);
  const [excelCurrent, setExcelCurrent] = useState(null);
  const [excelApplying, setExcelApplying] = useState(false);
  const [excelDiffTab, setExcelDiffTab] = useState("fmlm");
  const uploadInputRef = useRef(null);

  const downloadCard = async () => {
    try {
      const [fmlmR, mmR, odaR] = await Promise.all([
        api.get("/v2/admin-config/fm-lm-rates").catch(() => ({ data: {} })),
        api.get("/v2/mm-matrix").catch(() => ({ data: { grid: {} } })),
        api.get("/v2/oda/pincodes", { params: { limit: 500 } }).catch(() => ({ data: [] })),
      ]);
      const fm = fmlmR.data?.fm || {};
      const lm = fmlmR.data?.lm || {};
      const mmGrid = mmR.data?.grid || {};
      const odaPincodes = Array.isArray(odaR.data) ? odaR.data : (odaR.data?.items || []);
      // B2B + B2C exported from current frontend display values (contracted, read-only)
      const wb = buildRateCardWorkbook({
        fm, lm, mmGrid,
        b2bMatrix: B2B_MATRIX,
        b2cRates: {},
        odaPincodes,
      });
      downloadRateCard(wb);
      toast.success("Rate card downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    try {
      const [fmlmR, mmR, odaR] = await Promise.all([
        api.get("/v2/admin-config/fm-lm-rates").catch(() => ({ data: {} })),
        api.get("/v2/mm-matrix").catch(() => ({ data: { grid: {} } })),
        api.get("/v2/oda/pincodes", { params: { limit: 500 } }).catch(() => ({ data: [] })),
      ]);
      const current = {
        fm: fmlmR.data?.fm || {},
        lm: fmlmR.data?.lm || {},
        mmGrid: mmR.data?.grid || {},
        odaPincodes: Array.isArray(odaR.data) ? odaR.data : (odaR.data?.items || []),
      };
      const { valid, errors, parsed } = await parseRateCardFile(file);
      setExcelCurrent(current);
      setExcelParsed(parsed || null);
      if (!valid) {
        setExcelErrors(errors);
        setExcelDiff(null);
        setExcelModalOpen(true);
        return;
      }
      const diff = {
        fmlm: diffFmLm({ fm: current.fm, lm: current.lm }, { fm: parsed.fm, lm: parsed.lm }),
        mm: diffMm(current.mmGrid, parsed.mmGrid),
        oda: diffOda(current.odaPincodes, parsed.odaPincodes),
      };
      setExcelDiff(diff);
      setExcelErrors(null);
      setExcelDiffTab("fmlm");
      setExcelModalOpen(true);
    } catch (e) {
      toast.error("Failed to parse Excel file");
    }
  };

  const applyChanges = async () => {
    if (!excelParsed || !excelDiff) return;
    setExcelApplying(true);
    let applied = 0;
    try {
      // 1) FM/LM if any row changed
      const fmlmChanged = excelDiff.fmlm.some((d) => d.changed);
      if (fmlmChanged) {
        await api.put("/v2/admin-config/fm-lm-rates", { fm: excelParsed.fm, lm: excelParsed.lm });
        applied += excelDiff.fmlm.filter((d) => d.changed).length;
      }
      // 2) MM cells — one PUT per changed cell
      const mmChanges = excelDiff.mm.filter((d) => d.changed);
      for (const c of mmChanges) {
        await api.put(`/v2/mm-matrix/${c.band}/${c.slab}`, {
          rate_per_kg: Number(excelParsed.mmGrid[c.band][c.slab]),
        });
      }
      applied += mmChanges.length;

      // 3) ODA — bulk upload via file (only if changes)
      const odaChanges = excelDiff.oda.filter((d) => d.changed);
      if (odaChanges.length > 0) {
        // Build a tiny CSV/XLSX upload from parsed list; backend accepts xlsx
        try {
          const XLSX = await import("xlsx");
          const aoa = [
            ["pincode", "city", "state", "type", "surcharge_per_kg", "min_surcharge", "is_manual_override", "is_active"],
            ...excelParsed.odaPincodes.map((p) => [
              p.pincode, p.city, p.state, p.type, p.surcharge_per_kg, p.min_surcharge,
              p.is_manual_override ? "YES" : "NO", p.is_active ? "YES" : "NO",
            ]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(aoa);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "ODA_Pincodes");
          const blob = new Blob(
            [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
            { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
          );
          const fd = new FormData();
          fd.append("file", blob, "oda.xlsx");
          await api.post("/v2/oda/upload/excel", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          applied += odaChanges.length;
        } catch (err) {
          toast.error("ODA upload failed");
        }
      }
      toast.success(`Rate card updated successfully. ${applied} changes applied.`);
      setExcelModalOpen(false);
      setExcelDiff(null);
      setExcelParsed(null);
      setExcelCurrent(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    }
    setExcelApplying(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Rate Configuration</h1>
            <p className="text-slate-500 text-sm mt-0.5">Manage pricing rules for own network and 3PL</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={downloadCard}
              data-testid="rate-card-download-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold rounded"
            >
              <Download className="w-3.5 h-3.5" /> Download Rate Card
            </button>
            <button
              onClick={() => uploadInputRef.current?.click()}
              data-testid="rate-card-upload-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold rounded"
            >
              <Upload className="w-3.5 h-3.5" /> Upload Rate Card
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => onUploadFile(e.target.files?.[0])}
              data-testid="rate-card-upload-input"
            />
          </div>
        </div>

        {/* Pill tabs */}
        <div className="inline-flex p-1 bg-slate-100 rounded-full" data-testid="rate-engine-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "fmlm" && <FmLmTab />}
        {tab === "mm" && <MMMatrixTab />}
        {tab === "tpl" && <TplTab />}
        {tab === "products" && <ProductsTab />}
      </div>

      {excelModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3" data-testid="rate-card-modal">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {excelErrors ? "Upload Validation Errors" : "Rate Card Changes Preview"}
              </h3>
              <button onClick={() => setExcelModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {excelErrors ? (
                <div className="space-y-2" data-testid="rate-card-errors">
                  <div className="text-sm text-red-700">Found {excelErrors.length} validation error(s):</div>
                  <table className="w-full text-xs border border-red-100">
                    <thead className="bg-red-50 text-red-800">
                      <tr>
                        <th className="text-left p-2">Sheet</th>
                        <th className="text-left p-2">Cell</th>
                        <th className="text-left p-2">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-50">
                      {excelErrors.map((e, idx) => (
                        <tr key={idx}>
                          <td className="p-2 font-mono">{e.sheet}</td>
                          <td className="p-2 font-mono">{e.cell}</td>
                          <td className="p-2">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : excelDiff ? (
                <>
                  <div className="flex items-center gap-1 mb-3" data-testid="rate-card-diff-tabs">
                    {[
                      { k: "fmlm", l: "FM/LM" },
                      { k: "mm", l: "MM Matrix" },
                      { k: "b2b", l: "B2B" },
                      { k: "b2c", l: "B2C" },
                      { k: "oda", l: "ODA" },
                    ].map((t) => (
                      <button
                        key={t.k}
                        onClick={() => setExcelDiffTab(t.k)}
                        data-testid={`diff-tab-${t.k}`}
                        className={`px-3 py-1 text-xs font-semibold rounded ${
                          excelDiffTab === t.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {t.l}
                      </button>
                    ))}
                  </div>
                  {(excelDiffTab === "b2b" || excelDiffTab === "b2c") && (
                    <div className="p-3 rounded bg-amber-50 border border-amber-100 text-xs text-amber-700">
                      B2B and B2C Delhivery rates cannot be changed via upload. Contact Sendtoo tech team.
                    </div>
                  )}
                  {excelDiffTab === "fmlm" && <DiffTable rows={excelDiff.fmlm} cols={["field", "current", "incoming"]} />}
                  {excelDiffTab === "mm" && <DiffTable rows={excelDiff.mm} cols={["field", "current", "incoming"]} summary={`MM Matrix: ${excelDiff.mm.filter((r) => r.changed).length} cells changing, ${excelDiff.mm.filter((r) => !r.changed).length} unchanged`} />}
                  {excelDiffTab === "oda" && <DiffTable rows={excelDiff.oda} cols={["field", "current", "incoming"]} summary={`ODA: ${excelDiff.oda.filter((r) => r.changed).length} pincodes changing`} />}
                </>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
              <button
                onClick={() => setExcelModalOpen(false)}
                data-testid="rate-card-cancel-btn"
                className="px-4 py-1.5 border border-slate-200 text-slate-700 text-xs font-semibold rounded"
              >
                Cancel
              </button>
              {excelDiff && !excelErrors && (
                <button
                  onClick={applyChanges}
                  disabled={excelApplying}
                  data-testid="rate-card-apply-btn"
                  className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50"
                >
                  {excelApplying ? "Applying..." : "Apply Changes"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function DiffTable({ rows, cols, summary }) {
  return (
    <div className="space-y-2">
      {summary && <div className="text-xs text-slate-600 font-semibold">{summary}</div>}
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase">
            <tr>
              <th className="text-left p-2 font-semibold">Field/Cell</th>
              <th className="text-left p-2 font-semibold">Current</th>
              <th className="text-left p-2 font-semibold">New</th>
              <th className="text-center p-2 font-semibold">Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className={r.changed ? "bg-amber-50" : "bg-white"} data-testid={`diff-row-${idx}`}>
                <td className="p-2 font-mono text-slate-800">{r.field}</td>
                <td className="p-2 font-mono text-slate-500">{String(r.current)}</td>
                <td className="p-2 font-mono text-slate-800">{String(r.incoming)}</td>
                <td className="p-2 text-center">
                  {r.changed ? <span className="text-amber-700 font-bold">●</span> : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
